<template>
  <transition
    name="fadeIn"
    enter-active-class="animated fadeInDown"
    leave-active-class="animated fadeOutUp"
  >
    <section :class="['search', { 'search--negative': negative }]">
      <div class="search__input">
        <input
          ref="input"
          type="text"
          :placeholder="$t('search.placeholder')"
          v-debounce:500="search"
          debounce-events="input"
          spellcheck="false"
        />
        <close-icon class="search__input--close" v-if="!negative" @click="toggleSearch" />
        <white-close-icon class="search__input--close" v-if="negative" @click="toggleSearch" />

        <div class="search__input__results">
          <p v-show="filters.hasOwnProperty('customers') && !fetching">
            {{ results.length }} {{ $tc("search.results", 10) }}.
          </p>

          <div
            class="search__input__results--filter"
            @click="showFilters = !showFilters"
            v-show="filters.hasOwnProperty('customers') && !fetching"
          >
            {{ showFilters ? "-" : "+" }} {{ $t("search.filter") }}
          </div>
        </div>

        <transition
          enter-active-class="animated fadeIn"
          leave-active-class="animated fadeOut"
          mode="out-in"
        >
          <div
            class="search__input__filters"
            v-show="showFilters && !fetching && filters.hasOwnProperty('customers')"
          >
            <div class="search__input__filters__content">
              <sui-grid :columns="4" class="search__input__filters__content__columns">
                <sui-grid-row>
                  <sui-grid-column
                    v-for="(filterColumn, key) in filters"
                    class="filter__column"
                    :key="key"
                  >
                    <ul>
                      <li class="title">/ {{ $t(`search.columns.${key}`) }}</li>
                      <li
                        v-for="filter in filterColumn"
                        :key="filter.name"
                        :class="[
                          {
                            active: isOnFilter(filter.id || filter.regional_name || filter, key),
                          },
                        ]"
                        @click="filterSearch(filter.id || filter, key)"
                      >
                        / {{ filter.name || filter.regional_name || filter }}
                      </li>
                    </ul>
                  </sui-grid-column>
                </sui-grid-row>
              </sui-grid>
            </div>
          </div>
        </transition>
      </div>

      <sui-grid :columns="1" class="search__item-content">
        <sui-loader v-show="fetching" size="large" active centered inline />
        <sui-grid-row v-show="!fetching">
          <sui-grid-column v-if="showMessageFeedback">
            <h3 class="search--no-results">
              {{ errorMessage || $t("generals.jobsfounded") }}
            </h3>
          </sui-grid-column>
          <sui-grid-column v-else>
            <sui-grid
              :columns="4"
              v-for="i in Math.ceil(results.length / 4)"
              :key="i"
              class="search__item-content--row"
            >
              <sui-grid-row>
                <sui-grid-column
                  v-for="(result, index) in results.slice((i - 1) * 4, i * 4)"
                  :key="index"
                >
                  <transition-group
                    name="staggered-fade"
                    :css="false"
                    @before-enter="beforeEnter"
                    @enter="enter"
                    @leave="leave"
                  >
                    <grid-item
                      @click.native="toggleSearch"
                      :key="index"
                      :image="result.image"
                      :title="result.title"
                      :client="result.client"
                      :year="2020"
                      :country="result.regional_name"
                      theme="default"
                      :data-index="index"
                      :link="`/works/${$i18n.locale}/${result.slug}`"
                      v-show="itemsVisible"
                    />
                  </transition-group>
                </sui-grid-column>
              </sui-grid-row>
            </sui-grid>
          </sui-grid-column>
        </sui-grid-row>

        <paginate
          v-show="pagination.total > 32 && !fetching"
          :page-count="pagination.lastPage"
          :page-range="3"
          :margin-pages="2"
          :click-handler="paginate"
          :prev-text="'<'"
          :next-text="'>'"
          :container-class="'pagination'"
          :page-class="'page-item'"
        >
        </paginate>
      </sui-grid>
    </section>
  </transition>
</template>

<script>
import Velocity from "velocity-animate";
import CloseSVG from "../../assets/close.svg";
import WhiteCloseSVG from "../../assets/close-white.svg";

export default {
  name: "search",
  components: {
    "close-icon": CloseSVG,
    "white-close-icon": WhiteCloseSVG,
  },

  data() {
    return {
      showFilters: false,
      itemsVisible: false,
      results: [],
      fetching: false,
      keyword: "",
      filters: [],
      filterParams: {},
      errorMessage: null,
      pagination: {
        total: 0,
        perPage: 32,
        lastPage: 0,
      },
    };
  },

  mounted() {
    this.$hub.$on("searchInputFocus", () => {
      this.$nextTick(() => this.$refs.input.focus());
      this.itemsVisible = true;
    });
  },

  destroyed() {
    this.$hub.$off("searchInputFocus");
  },

  computed: {
    negative() {
      return this.$store.state.ui.negativeLayout;
    },

    showMessageFeedback() {
      return (
        (this.results.length === 0 && this.filters[0]?.hasOwnProperty("customers")) ||
        this.errorMessage
      );
    },
  },

  methods: {
    isOnFilter(id, key) {
      return this.filterParams[key] === id;
    },

    paginate(page) {
      this.filterParams.page = page;
      this.search(this.keyword, { page });

      $(".search").scrollTop(0);
    },

    filterSearch(id, key) {
      if (this.filterParams[key] === id) {
        delete this.filterParams[key];
      } else {
        this.filterParams[key] = id;
      }

      this.filterParams.page = 1;
      this.search(this.keyword, this.filterParams);
    },

    search(keyword, page) {
      if (!keyword || keyword === "") {
        this.results = [];
        return;
      }

      this.keyword = keyword;
      this.fetching = true;
      this.itemsVisible = false;
      this.errorMessage = null;

      this.$search
        .post(keyword, this.$store.state.ui.language, page)
        .then((resolve) => {
          this.results = resolve.data || [];
          this.pagination.lastPage = resolve.paginate.last || 0;
          this.pagination.total = resolve.paginate.total || 0;

          if (this.filters.length === 0) this.filters = resolve.filters;

          setTimeout(() => {
            this.itemsVisible = true;
          }, 10);
        })
        .catch(() => {
          this.errorMessage = this.$t("generals.error");
        })
        .finally(() => {
          this.fetching = false;
        });
    },

    toggleSearch() {
      this.$hub.$emit("toggleSearch");
      this.itemsVisible = !this.itemsVisible;
    },

    beforeEnter(el) {
      const $el = el;
      $el.style.opacity = 0;
    },

    enter(el, done) {
      const delay = el.dataset.index * 250;

      setTimeout(() => {
        Velocity(el, { opacity: 1 }, { complete: done });
      }, delay);
    },

    leave(el, done) {
      const delay = el.dataset.index * 150;

      setTimeout(() => {
        Velocity(el, { opacity: 0 }, { complete: done });
      }, delay);
    },
  },
};
</script>

<style lang="scss">
@import "./styles/search";
</style>
